// Third Parth
import React from 'react';
import classNames from 'classnames';

// Components
import { ErrorTooltip, Tooltip, InfoTooltip } from '../../../../components/tooltips';
import { UnitInput, DatePicker } from '../../../../components/inputs';
import LegacyBMOGIFPortfolioDropdown from '@/views/components/bmo-gif/legacy/LegacyBMOGIFPortfolioDropdown';
import LegacyBMOGIFYearAgeInputs from '@/views/components/bmo-gif/legacy/LegacyBMOGIFYearAgeInputs';

// Utils
import { getTotalGifPortfolioAllocation, isInvalidDepositAge } from '@/utils/bmo-gif/index';
import {
  BMOi18n,
  GIF_DEPOSIT_TYPES,
  GIF_CLASS_TYPES,
  SALES_CHARGES,
  GIF_PLAN_OPTIONS,
  MIN_PRESTIGE_DEPOSIT,
  INVALID_AGE_REASON,
  PROJECTION_BASIS_TYPES,
} from '@/utils/bmo-gif/constants';

import { FREQUENCIES } from '@/utils/constants';
import { getReadOnlyTooltipMessage } from '@/utils/comparison';
import { GenericEmptyState } from '../../../../components/misc';
import Comparison from '@/types/comparison';
import ComparisonContact from '@/types/comparison-contact';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import BMOGIFProjectionBasis from '@/types/bmo-gif/bmo-gif-projection-basis';
import BMOGIFProjectionBasisBenchmark from '@/types/bmo-gif/bmo-gif-projection-basis-benchmark';
import BMOGIFProjectionBasisCustom from '@/types/bmo-gif/bmo-gif-projection-basis-custom';

type LocalProps = {
  locale: string;
  portfolio: BMOGIFPortfolio;
  projectionBasisOptions?: BMOGIFProjectionBasis[];
  readOnly?: boolean;
  comparison: Comparison;
  contact: ComparisonContact;
};

type FunctionProps = {
  setDepositType: (value: string) => void;
  setDepositAmount: (value?: number) => void;
  setPurchaseDate: (value: Date) => void;
  setSubsequentDepositAmount: (value: number) => void;
  setSchemeType: (value: string) => void;
  setDepositStart: (value?: number) => void;
  setDepositEnd: (value?: number) => void;
  setNumYears: (value?: number) => void;
  setFrequency: (value: string) => void;
  setProjectionBasis: (value: { name: string; id: string }) => void;
  setSalesChargeType: (value: string) => void;
  setSalesChargeAmount: (value?: number) => void;
  setSystematicWithdrawalMonthEnd: (month?: number) => void;
  setSystematicWithdrawalMonthStart: (month?: number) => void;
  setCustomRateAmount: (value?: number) => void;
};

type Props = LocalProps & FunctionProps;

class LegacyBMOGIFAdditionalInfo extends React.Component<Props> {
  portfolioIsFull = (): boolean => {
    const { portfolio } = this.props;

    return getTotalGifPortfolioAllocation(portfolio) === 100;
  };

  isDepositAmountTooLow = () => {
    const {
      portfolio: { classType, depositAmount, planOption },
    } = this.props;

    if (
      depositAmount != null &&
      (classType === GIF_CLASS_TYPES.CLASS_A_PRESTIGE || (classType === GIF_CLASS_TYPES.CLASS_F_PRESTIGE && planOption === GIF_PLAN_OPTIONS.GIF7575))
    ) {
      return depositAmount < MIN_PRESTIGE_DEPOSIT;
    }

    return false;
  };

  isDepositAmountTooHigh = () => {
    const {
      portfolio: { classType, depositAmount, planOption },
    } = this.props;

    if (
      depositAmount != null &&
      (classType === GIF_CLASS_TYPES.CLASS_A || (classType === GIF_CLASS_TYPES.CLASS_F && planOption === GIF_PLAN_OPTIONS.GIF7575))
    ) {
      return depositAmount >= MIN_PRESTIGE_DEPOSIT;
    }

    return false;
  };

  invalidDepositAmount = () => this.isDepositAmountTooLow() || this.isDepositAmountTooHigh();

  renderInitialDeposit = () => {
    const {
      locale,
      portfolio: { depositType, depositAmount, purchaseDate },
      readOnly,
      comparison,
    } = this.props;

    const options = [
      {
        value: GIF_DEPOSIT_TYPES.LUMP_SUM,
        label: BMOi18n[locale].INITIAL_LUMP_SUM,
      },
      {
        value: GIF_DEPOSIT_TYPES.PAD,
        label: BMOi18n[locale].INITIAL_PAD,
      },
    ];

    const invalidDepositAge = comparison.contact && isInvalidDepositAge(this.props.portfolio, comparison.contact);
    const inValidInitialDeposit = !!invalidDepositAge && invalidDepositAge.isInvalid && invalidDepositAge.reason === INVALID_AGE_REASON.INITIAL_DEPOSIT;
    const disabledTooltipMessage = getReadOnlyTooltipMessage(comparison);
    const disabledInputs = readOnly;

    const getTooltipMessage = () => {
      if (this.isDepositAmountTooHigh()) {
        return BMOi18n[locale].INVALID_NON_PRESTIGE_AMOUNT;
      }
      if (this.isDepositAmountTooLow()) {
        return BMOi18n[locale].INVALID_PRESTIGE_AMOUNT;
      }

      return undefined;
    };

    return (
      <div className="initial-deposit-info d-flex align-items-center">
        <div className="initial-deposit d-flex flex-column">
          <LegacyBMOGIFPortfolioDropdown
            dark
            selected={depositType}
            onChange={this.props.setDepositType}
            options={options}
            disabled={disabledInputs}
            tooltipMessage={disabledTooltipMessage}
            tooltipDisabled={!disabledInputs}
          />

          <div className="d-flex align-items-center">
            {this.invalidDepositAmount() && <ErrorTooltip title={BMOi18n[locale].INVALID_AMOUNT} message={getTooltipMessage()} />}

            <Tooltip message={disabledTooltipMessage} disabled={!disabledInputs}>
              <span>
                <UnitInput
                  type="number"
                  unitSymbol="$"
                  dark
                  min={0}
                  value={depositAmount}
                  onValueChange={(value: string) => this.props.setDepositAmount(value ? Number(value) : undefined)}
                  error={this.invalidDepositAmount()}
                  disabled={disabledInputs}
                />
              </span>
            </Tooltip>
          </div>
        </div>

        <div className="d-flex flex-column flex-fill">
          <div className="input-title">
            <span>{BMOi18n[locale].PURCHASE_DATE}</span>
          </div>
          <div className="d-flex align-items-center">
            <Tooltip message={disabledTooltipMessage} disabled={!disabledInputs}>
              <span>
                <DatePicker
                  compact
                  dark
                  allowPrevNext2
                  minDate={new Date()}
                  onDateChange={this.props.setPurchaseDate}
                  defaultDate={new Date(purchaseDate)}
                  customFormat="DD/MM/YYYY"
                  disabled={disabledInputs}
                />
              </span>
            </Tooltip>
            {inValidInitialDeposit && <ErrorTooltip message={BMOi18n[locale].INVALID_DEPOSIT_AGE} />}
          </div>
        </div>
      </div>
    );
  };

  renderSubsequentDeposit = () => {
    const {
      locale,
      portfolio,
      portfolio: { subsequentDepositAmount, depositAmount, frequency },
      readOnly,
      comparison,
    } = this.props;

    const disabledInputs = depositAmount === undefined || readOnly;
    const tooltipMessage = readOnly ? getReadOnlyTooltipMessage(comparison) : BMOi18n[locale].INVALID_DEPOSIT_AMOUNT;
    const invalidDepositAge = comparison.contact && isInvalidDepositAge(this.props.portfolio, comparison.contact);
    const inValidSubsequentDeposit = !!invalidDepositAge && invalidDepositAge.isInvalid && invalidDepositAge.reason === INVALID_AGE_REASON.SUBSEQUENT_DEPOSIT;
    const frequencyValues = Object.keys(FREQUENCIES).map(option => ({
      value: FREQUENCIES[option],
      label: BMOi18n[locale][option],
    }));

    return (
      <div className="subsequent-deposit-info">
        <div className="d-flex">
          <div className="subsequent-deposit d-flex flex-column">
            <div className={classNames('input-title', { disabled: disabledInputs })}>
              <span>{BMOi18n[locale].SUBSEQUENT_DEPOSIT}</span>
            </div>

            <Tooltip message={tooltipMessage} disabled={!disabledInputs}>
              <span>
                <UnitInput
                  type="number"
                  unitSymbol="$"
                  dark
                  min={0}
                  value={subsequentDepositAmount || ''}
                  onValueChange={(value: string) => this.props.setSubsequentDepositAmount(Number(value))}
                  disabled={disabledInputs}
                />
              </span>
            </Tooltip>
          </div>

          <div className="frequency d-flex flex-fill">
            <LegacyBMOGIFPortfolioDropdown
              dark
              className="flex-fill"
              title={BMOi18n[locale].FREQUENCY}
              placeholder={BMOi18n[locale].SELECT_FREQUENCY}
              selected={frequency}
              onChange={this.props.setFrequency}
              options={frequencyValues}
              disabled={disabledInputs}
              tooltipMessage={tooltipMessage}
              tooltipDisabled={!disabledInputs}
            />
          </div>
        </div>

        <LegacyBMOGIFYearAgeInputs
          locale={locale}
          dark
          selectedScheme={portfolio.scheme}
          start={portfolio.start}
          end={portfolio.end}
          numYears={portfolio.numYears}
          term={portfolio.term}
          title={BMOi18n[locale].YEAR_CALCULATION}
          setNumYears={this.props.setNumYears}
          setStart={this.props.setDepositStart}
          setEnd={this.props.setDepositEnd}
          setSchemeType={this.props.setSchemeType}
          disabledInputs={disabledInputs}
          dob={portfolio.dob}
          readOnly={readOnly}
          comparison={comparison}
          purchaseDate={portfolio.purchaseDate}
          setMonthEnd={this.props.setSystematicWithdrawalMonthEnd}
          setMonthStart={this.props.setSystematicWithdrawalMonthStart}
          inValidSubsequentDeposit={inValidSubsequentDeposit}
        />
      </div>
    );
  };

  onSalesChargeAmountChange = (value: number) => {
    this.props.setSalesChargeAmount(Math.min(value, 5));
  };

  onCustomRateAmountChange = (value: number) => {
    this.props.setCustomRateAmount(Math.min(value, 12));
  };

  renderDropdowns = () => {
    const {
      locale,
      portfolio: { planOption, depositAmount, projectionBasis, salesChargeType, salesChargeAmount, customRate },
      projectionBasisOptions,
      readOnly,
      comparison,
    } = this.props;

    const disabledInputs = depositAmount === undefined || readOnly;
    const tooltipMessage = readOnly ? getReadOnlyTooltipMessage(comparison) : BMOi18n[locale].INVALID_DEPOSIT_AMOUNT;

    const projectionBasisValues =
      projectionBasisOptions &&
      projectionBasisOptions.map((option: BMOGIFProjectionBasis) => {
        let value = {
          name: option.name,
          id: option.id,
          type: option.type,
        };
        switch (option.type) {
          case PROJECTION_BASIS_TYPES.BENCHMARK: {
            value = { ...value, refId: option.refId } as BMOGIFProjectionBasisBenchmark;
            break;
          }
          case PROJECTION_BASIS_TYPES.CUSTOM: {
            value = { ...value } as BMOGIFProjectionBasisCustom;
            break;
          }
          default: {
            value = { ...value };
          }
        }
        return {
          value,
          label: BMOi18n[locale][option.name],
        };
      });

    let salesChargeValues = Object.keys(SALES_CHARGES).map(option => ({
      value: SALES_CHARGES[option],
      label: BMOi18n[locale][option],
    }));

    // Deferred Sales Charge has been banned in Canada since June 1, 2022, so we remove it for new comparisons
    if (!readOnly) {
      salesChargeValues = salesChargeValues.filter(option => option.value !== SALES_CHARGES.DSC);
    }

    return (
      <div className="additional-info-dropdowns">
        <div className="d-flex">
          <LegacyBMOGIFPortfolioDropdown
            dark
            className="volatility"
            title={BMOi18n[locale].VOLATILITY}
            placeholder={BMOi18n[locale].SELECT_PROJECTION_BASIS}
            selected={projectionBasis}
            onChange={this.props.setProjectionBasis}
            options={projectionBasisValues || []}
            disabled={disabledInputs}
            tooltipMessage={tooltipMessage}
            tooltipDisabled={!disabledInputs}
          />
          {projectionBasis && projectionBasis.type === PROJECTION_BASIS_TYPES.CUSTOM && (
            <div className="ror-amount d-flex flex-column">
              <div className="input-title">
                <span>{BMOi18n[locale].RATE_OF_RETURN}</span>
              </div>
              <div className="d-flex">
                <UnitInput
                  type="number"
                  unitSymbol="%"
                  dark
                  min={0}
                  max={12}
                  step={0.01}
                  disabled={disabledInputs}
                  value={customRate && customRate.toString()}
                  onValueChange={(value: string) => this.onCustomRateAmountChange(Number(value))}
                />

                <InfoTooltip message={BMOi18n[locale].ROR_AMOUNT_INFO} />
              </div>
            </div>
          )}
        </div>

        <div className="d-flex">
          <LegacyBMOGIFPortfolioDropdown
            dark
            className="sales-charge-type"
            title={BMOi18n[locale].SALES_CHARGES}
            selected={salesChargeType}
            onChange={this.props.setSalesChargeType}
            options={salesChargeValues}
            disabled={disabledInputs || planOption === GIF_PLAN_OPTIONS.GIF75100PLUS}
            tooltipMessage={tooltipMessage}
            tooltipDisabled={planOption === GIF_PLAN_OPTIONS.GIF75100PLUS || !disabledInputs}
          />

          {salesChargeType === SALES_CHARGES.FEL && (
            <div className="sales-charge-amount d-flex">
              <Tooltip message={tooltipMessage} disabled={planOption === GIF_PLAN_OPTIONS.GIF75100PLUS || !disabledInputs}>
                <span>
                  <UnitInput
                    type="number"
                    unitSymbol="%"
                    dark
                    min={0}
                    max={5}
                    step={0.01}
                    value={salesChargeAmount && salesChargeAmount.toString()}
                    onValueChange={(value: string) => this.onSalesChargeAmountChange(Number(value))}
                    disabled={disabledInputs || planOption === GIF_PLAN_OPTIONS.GIF75100PLUS}
                  />
                </span>
              </Tooltip>

              {planOption !== GIF_PLAN_OPTIONS.GIF75100PLUS && <InfoTooltip message={BMOi18n[locale].SALES_CHARGE_AMOUNT_INFO} />}
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { locale } = this.props;

    return (
      <div className="bmo-gif-additional-info d-flex flex-column flex-fill">
        <div className="additional-info-header d-flex align-items-center">
          <div className="flex-fill">
            <i className="icon-paperclip" />

            {BMOi18n[locale].ADDITIONAL_INFO}
          </div>
        </div>

        {!this.portfolioIsFull() ? (
          <GenericEmptyState body={BMOi18n[locale].ADDITIONAL_INFO_EMPTY} />
        ) : (
          <div className="additional-info-content">
            <div className="additional-info-content d-flex flex-column">
              {this.renderInitialDeposit()}

              {this.renderSubsequentDeposit()}

              {this.renderDropdowns()}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default LegacyBMOGIFAdditionalInfo;
